export type TrackMediaStatus = "notStarted" | "inProgress" | "completed";

export type TrackProgressEntry = {
  status: TrackMediaStatus;
  percent: number;
  filetype?: "video" | "audio" | "pdf" | null;
  timemodified?: number;
};

const safeDecode = (url: string) => {
  try {
    return decodeURIComponent(url).replace(/\\/g, "");
  } catch {
    return url.replace(/\\/g, "");
  }
};

export const normalizeTrackUrl = (url: string) => {
  if (!url) return "";
  try {
    const u = new URL(safeDecode(url));
    return `${u.origin}${u.pathname}`;
  } catch {
    return safeDecode(url).split("?")[0].split("#")[0].trim();
  }
};

export const getTrackUrlVariants = (url: string) => {
  if (!url) return [];
  const decoded = safeDecode(url);
  return Array.from(
    new Set(
      [
        normalizeTrackUrl(url),
        decoded,
        decoded.split("?")[0].split("#")[0].trim(),
        normalizeTrackUrl(decoded),
      ].filter(Boolean)
    )
  );
};

/** Tính trạng thái video/audio từ playtime & duration (hỗ trợ số thập phân). */
export const getMediaProgressFromPlaytime = (
  playtimeRaw: number | string,
  durationRaw: number | string
): Pick<TrackProgressEntry, "status" | "percent"> => {
  const playtime = Number(playtimeRaw) || 0;
  const duration = Number(durationRaw) || 0;

  if (duration <= 0) {
    if (playtime > 0) {
      return { status: "inProgress", percent: 0 };
    }
    return { status: "notStarted", percent: 0 };
  }

  const percent = Math.min(100, Math.round((playtime / duration) * 100));
  // Làm tròn giây: 71 / 71.55 => floor bằng nhau => coi như hoàn thành
  const isCompleted =
    Math.floor(playtime) >= Math.floor(duration) ||
    playtime >= duration - 0.5 ||
    percent >= 99;

  if (isCompleted) {
    return { status: "completed", percent: 100 };
  }
  if (playtime > 0) {
    return { status: "inProgress", percent };
  }
  return { status: "notStarted", percent: 0 };
};

const shouldReplaceEntry = (
  existing: TrackProgressEntry | undefined,
  next: TrackProgressEntry
) => {
  if (!existing) return true;
  if (next.percent !== existing.percent) {
    return next.percent > existing.percent;
  }
  return (next.timemodified || 0) >= (existing.timemodified || 0);
};

/** Build map URL -> progress từ danh sách track API (có filetype). */
export const buildMediaStatusMap = (
  trackList: any[] | null | undefined
): Record<string, TrackProgressEntry> => {
  if (!trackList?.length) return {};

  return trackList.reduce((map: Record<string, TrackProgressEntry>, item: any) => {
    const filetype =
      item.filetype || (item.pdfid ? "pdf" : item.videoid ? "video" : null);

    if (filetype === "pdf") {
      if (typeof item.pdfid !== "string" || !item.pdfid.startsWith("http")) {
        return map;
      }
      const isCompleted = Boolean(item.iscompleted);
      const entry: TrackProgressEntry = {
        status: isCompleted ? "completed" : "notStarted",
        percent: isCompleted ? 100 : 0,
        filetype: "pdf",
        timemodified: Number(item.timemodified) || Number(item.timecreated) || 0,
      };
      getTrackUrlVariants(item.pdfid).forEach((key) => {
        if (shouldReplaceEntry(map[key], entry)) {
          map[key] = entry;
        }
      });
      return map;
    }

    if (
      (filetype === "video" || filetype === "audio" || !filetype) &&
      typeof item.videoid === "string" &&
      item.videoid.startsWith("http")
    ) {
      const progress = getMediaProgressFromPlaytime(item.playtime, item.duration);
      const entry: TrackProgressEntry = {
        ...progress,
        filetype: (filetype || "video") as "video" | "audio",
        timemodified: Number(item.timemodified) || Number(item.timecreated) || 0,
      };
      getTrackUrlVariants(item.videoid).forEach((key) => {
        if (shouldReplaceEntry(map[key], entry)) {
          map[key] = entry;
        }
      });
    }

    return map;
  }, {});
};

export const lookupMediaProgress = (
  mediaStatusMap: Record<string, TrackProgressEntry>,
  mediaUrl: string
): TrackProgressEntry | null => {
  const tryKeys = getTrackUrlVariants(mediaUrl);
  for (const key of tryKeys) {
    if (mediaStatusMap[key]) return mediaStatusMap[key];
  }

  // Fallback: khớp theo pathname đã normalize
  const normalized = normalizeTrackUrl(mediaUrl);
  if (normalized && mediaStatusMap[normalized]) {
    return mediaStatusMap[normalized];
  }

  return null;
};
